/**
 * useReEngagement Hook (DD-160)
 *
 * Detects returning users who have been inactive for >7 days and
 * controls visibility of the "What's New" modal.
 *
 * Logic:
 * 1. Fetch profiles.last_seen_at for the current user via get-onboarding
 * 2. If last_seen_at is >7 days ago, mark as inactive
 * 3. Check if user already dismissed the latest What's New version
 * 4. Check re_engagement_whats_new_enabled feature flag
 * 5. If all conditions met, show the modal
 *
 * The dismissed version is persisted in AsyncStorage so the modal
 * only shows once per release cycle.
 *
 * PostHog Events:
 * - re_engagement_modal_shown (days_inactive, version)
 * - re_engagement_modal_dismissed (version)
 * - re_engagement_feature_tapped (version, feature_title, route)
 *
 * DTO migration (Wave 4A): the get-onboarding response is parsed through
 * the shared `OnboardingStatusDto` via `fetchJsonValidated`, so
 * `last_seen_at` is now a typed field (was silently `undefined` in the
 * previous raw-fetch path).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { differenceInDays } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { OnboardingStatusDto } from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { useFeatureFlag } from './useFeatureFlags';
import { captureEvent } from '../lib/analytics';
import { getLatestVersion } from '../lib/whats-new-content';

/** AsyncStorage key for the last dismissed What's New version */
const DISMISSED_VERSION_KEY = 'whats_new_dismissed_version';

/** Minimum days of inactivity before showing the modal */
const INACTIVITY_THRESHOLD_DAYS = 7;

/** Stable noops for anonymous users — avoids new closures on every render. */
const NOOP_DISMISS = async (): Promise<void> => {};
const NOOP_FEATURE_TAP = (): void => {};

/** Query key for last_seen_at */
const LAST_SEEN_QUERY_KEY = ['re-engagement', 'last-seen'] as const;

export interface ReEngagementState {
  /** Whether the What's New modal should be visible */
  showWhatsNew: boolean;
  /** Number of days since the user last used the app */
  daysInactive: number;
  /** Dismiss the modal and persist the dismissed version */
  dismissWhatsNew: () => Promise<void>;
  /** Handle a feature card tap */
  onFeatureTap: (featureTitle: string, route?: string) => void;
  /** Whether the hook is still loading data */
  isLoading: boolean;
}

export function useReEngagement(): ReEngagementState {
  const { accessToken, user } = useAuth();
  const featureFlag = useFeatureFlag('re_engagement_whats_new_enabled');
  const isEnabled = featureFlag !== false; // kill switch pattern: default ON

  const hasShownRef = useRef(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [daysInactive, setDaysInactive] = useState(0);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [dismissedLoaded, setDismissedLoaded] = useState(false);

  const latestVersion = getLatestVersion();

  // Load dismissed version from AsyncStorage
  useEffect(() => {
    AsyncStorage.getItem(DISMISSED_VERSION_KEY)
      .then((v) => {
        setDismissedVersion(v);
        setDismissedLoaded(true);
      })
      .catch(() => {
        setDismissedLoaded(true);
      });
  }, []);

  // Fetch last_seen_at via get-onboarding handler (returns profiles data)
  const { data: lastSeenAt, isLoading: queryLoading } = useQuery({
    queryKey: [...LAST_SEEN_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      try {
        const dto = await fetchJsonValidated(
          OnboardingStatusDto,
          getEdgeFunctionUrl('get-onboarding'),
        );
        return dto.last_seen_at ?? null;
      } catch {
        console.warn('[useReEngagement] Failed to fetch last_seen_at');
        return null;
      }
    },
    // Phase 2 (NocoDB #3844): skip for anon BA users. Re-engagement is an
    // authed-only feature; anon callers get 401 from strict `auth` route.
    enabled: !!accessToken && !!user?.id && !user.isAnonymous && isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,
  });

  // Evaluate whether to show the modal
  useEffect(() => {
    if (!isEnabled || !dismissedLoaded || queryLoading) return;
    if (!accessToken || !user?.id || user.isAnonymous) return;

    // Already dismissed this version
    if (dismissedVersion === latestVersion) return;

    // null last_seen_at: brand-new account or column just added.
    // Treat as active (just joined) — don't show re-engagement to new users.
    const daysSince = lastSeenAt
      ? differenceInDays(new Date(), new Date(lastSeenAt))
      : 0;
    setDaysInactive(daysSince);

    if (daysSince >= INACTIVITY_THRESHOLD_DAYS) {
      setShowWhatsNew(true);

      if (hasShownRef.current) return;
      hasShownRef.current = true;
      captureEvent('re_engagement_modal_shown', {
        days_inactive: daysSince,
        version: latestVersion,
      });
    }
  }, [
    isEnabled,
    accessToken,
    user?.id,
    lastSeenAt,
    dismissedVersion,
    dismissedLoaded,
    queryLoading,
    latestVersion,
  ]);

  const dismissWhatsNew = useCallback(async () => {
    setShowWhatsNew(false);

    captureEvent('re_engagement_modal_dismissed', {
      version: latestVersion,
    });

    try {
      await AsyncStorage.setItem(DISMISSED_VERSION_KEY, latestVersion);
      setDismissedVersion(latestVersion);
    } catch (err) {
      console.warn('[useReEngagement] Failed to persist dismissed version:', err);
    }
  }, [latestVersion]);

  const onFeatureTap = useCallback(
    (featureTitle: string, route?: string) => {
      captureEvent('re_engagement_feature_tapped', {
        version: latestVersion,
        feature_title: featureTitle,
        route: route ?? null,
      });
      // Navigation deferred: cards dismiss modal only. Deep linking to feature
      // routes will be added when router is accessible from this hook context.
    },
    [latestVersion],
  );

  // Anonymous or no session: no re-engagement.
  // Phase 2 (NocoDB #3844): anon BA users also skip.
  if (!accessToken || !user?.id || user.isAnonymous) {
    return {
      showWhatsNew: false,
      daysInactive: 0,
      dismissWhatsNew: NOOP_DISMISS,
      onFeatureTap: NOOP_FEATURE_TAP,
      isLoading: false,
    };
  }

  return {
    showWhatsNew,
    daysInactive,
    dismissWhatsNew,
    onFeatureTap,
    isLoading: queryLoading || !dismissedLoaded,
  };
}
